import { useCallback, useEffect, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { useOrderApi } from "@/hooks/apiHooks/useOrderApi";
import { meOrdersFetchErrorAtom } from "@/store/orderAtoms";
import type { GetMeOrdersResponse } from "@models/order";

// Shared fetch/sort/retry logic for the me-scoped order list, used by both
// AccountDashboard's Purchase History section and the OrderHistory page.
export const useMeOrders = () => {
    const { getMeOrders } = useOrderApi();
    const hasError = useAtomValue(meOrdersFetchErrorAtom);

    const [orders, setOrders] = useState<GetMeOrdersResponse[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const loadOrders = useCallback(
        (signal?: { cancelled: boolean }) => {
            // The hook drives the error atom; swallow the rejection here so it
            // doesn't surface as an unhandled promise rejection.
            getMeOrders()
                .then((result) => {
                    if (!signal?.cancelled) setOrders(result);
                })
                .catch(() => {
                    /* error atom drives the UI */
                })
                .finally(() => {
                    if (!signal?.cancelled) setIsLoading(false);
                });
        },
        [getMeOrders],
    );

    useEffect(() => {
        const signal = { cancelled: false };
        loadOrders(signal);
        return () => {
            signal.cancelled = true;
        };
    }, [loadOrders]);

    const sortedOrders = useMemo(
        () =>
            [...orders].sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
            ),
        [orders],
    );

    // setState is fine in an event handler (vs. synchronously in the effect,
    // which react-hooks/set-state-in-effect rejects) — show the spinner again
    // while the retry is in flight.
    const retry = () => {
        setIsLoading(true);
        loadOrders();
    };

    return { sortedOrders, isLoading, hasError, retry };
};
